import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { get, merge, forEach, map, find, isEmpty } from 'lodash';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';
import { getLabel } from 'utils/getLabel';
import { getVisibleItems } from 'resolvers/getVisibleEntities';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc MemberDetailsOrdersEntityWidget defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberDetailsOrdersEntityWidgetService } from 'services/memberDetailsOrdersEntityWidget';
 *
 * // create service instance and send/cancel request
 * const memberDetailsOrdersEntityWidgetService = new MemberDetailsOrdersEntityWidgetService();
 * const result = await memberDetailsOrdersEntityWidgetService.fetchData( '<user-id>' ); // fetch data
 * memberDetailsOrdersEntityWidgetService.cancel(); // cancel an active request
 */
export class MemberDetailsOrdersEntityWidgetService extends HttpService {

    // make request and fetch data
    async fetchData( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            const orderOption = get( response, 'body.component.data.options.orderStatus', [] );
            forEach( get( response, 'body.component.data.state.items', [] ), item => {
                item.values.items = map( item.values.items, innerItem => {
                    if( ! isEmpty( innerItem.values.status.orderStatus ) ) {
                        innerItem.values.status.orderStatus = getLabel( get( find( orderOption, val => val.name === innerItem.values.status.orderStatus ), 'title', {} ) );
                    }
                    return innerItem;
                } );
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: get( response, 'body.component', {} ),
            };
            
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // make request and fetch data
    async cancelOrder( { url = {}, data = {} } = {} ) {
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                    },
                } )
            } );

            if( SERVICE_RESPONSE_STATUS.FAILURE === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {

                return {
                    status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                    resData: get( response, 'body.component.data', {} )
                };
            }

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            };
            
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // make request and fetch data
    async editOrder( { url = {}, data = {} } = {} ) {
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                    },
                } ),
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: null,
            };
            
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // make request and fetch data
    async reOrder( { url = {}, data = {} } = {} ) {
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                    },
                } ),
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: null,
            };
            
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // make request and fetch data
    async initiateMarkResolved( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            if( SERVICE_RESPONSE_STATUS.FAILURE === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {

                return {
                    status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                    resData: {
                        description: get( response, 'body.component.data.description', {} ),
                    }
                };
            }

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    // make request and fetch data for Notes
    async initNotes( { url, data } ) {
       try {
           const response = await this.post( {
               host: CONFIG.host,
               path: url,
               data,
           } );

           const actions = get( response, 'body.component.structure.actions', [] );
           const title = get( response, 'body.component.structure.title', '' );
           const widgets = get( response, 'body.component.structure.widgets', '' );
           const formActions = get( response, 'body.component.structure.formActions', [] );
           const fields = get( response, 'body.component.structure.fields', '' );
           const options = get( response, 'body.component.data.options', {} );
           const visibility = get( response, 'body.component.visibility', {} );

           const _actions = getVisibleItems( actions, visibility );

           return {
               status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
               data: { widgets, actions: _actions, title, formActions, fields, options },
           };
           
       } catch( errResponse ) {
           return this._errorFormatter( errResponse );
       }
    }

   
    // Fetch Order NOtes
    async initOrderNotes( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            const actions = get( response, 'body.component.structure.actions', [] );
            const formActions = get( response, 'body.component.structure.formActions', [] );
            const title = get( response, 'body.component.structure.title', '' );
            const displayDateText = get( response, 'body.component.structure.displayDateText', '' );
            const fields = get( response, 'body.component.structure.fields', '' );
            const items = get( response, 'body.component.data.state.items', '' );
            const options = get( response, 'body.component.data.options', {} );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: { items, actions, title, displayDateText, fields, formActions, options },
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    // Refresh Order
    async refreshOrder( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: get( response, 'body.component.data.state.items', [] ),
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    async fetchOrderToDoOptions( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            const visibility = get( response, 'body.component.data.visibility', [] );
            const fields = get( response, 'body.component.structure.fields', [] );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    ...get( response, 'body.component.data', [] ),
                    ...get( response, 'body.component.structure', [] ),
                    primaryFields: getVisibleItems( fields, visibility )
                }
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    async trackingNumber( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );
            console.log(props)
            const visibility = get( response, 'body.component.data.visibility', [] );
            const fields = get( response, 'body.component.structure.fields', [] );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    ...get( response, 'body.component.data', [] ),
                    ...get( response, 'body.component.structure', [] ),
                    primaryFields: getVisibleItems( fields, visibility )
                }
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
     }


    async fetchControlledDrugReOrderInfo( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            const visibility = get( response, 'body.component.data.visibility', [] );
            const columns = get( response, 'body.component.structure.primaryColumns', [] );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    ...get( response, 'body.component.data', [] ),
                    ...get( response, 'body.component.structure', [] ),
                    primaryColumns: getVisibleItems( columns, visibility )
                }
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    // format error response
    _errorFormatter( errResponse ) {
        return errResponse; // some logic
    }
}
